import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subject } from 'rxjs';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Payment3dsService } from 'src/app/services/payment-3ds.service';

declare var alertify: any;

@Component({
  selector: 'app-tds-management',
  templateUrl: './tds-management.component.html',
  styleUrls: ['./tds-management.component.css'],
})
export class TdsManagementComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  // Tab activo
  activeTab: 'transactions' | 'config' = 'transactions';

  // KPIs
  stats: any = {};
  loadingStats = false;

  // Transacciones
  transactions: any[] = [];
  loadingTransactions = false;
  pagination = { page: 1, limit: 20, total: 0, pages: 0 };

  // Filtros
  filters = {
    status: '',
    dateFrom: '',
    dateTo: '',
    search: '',
  };

  // Detalle
  selectedTransaction: any = null;
  auditTrail: any[] = [];
  loadingDetail = false;

  // Config
  config: any = {};
  loadingConfig = false;

  // Statuses para el select
  statuses = [
    { value: '', label: 'Todos' },
    { value: 'INITIATED', label: 'Iniciada' },
    { value: 'AUTHENTICATED', label: 'Autenticada' },
    { value: 'CHALLENGE_PENDING', label: 'Challenge Pendiente' },
    { value: 'CHALLENGE_COMPLETED', label: 'Challenge Completado' },
    { value: 'PENDING_SALE', label: 'Venta Pendiente' },
    { value: 'SALE_COMPLETED', label: 'Completada' },
    { value: 'SALE_FAILED', label: 'Fallida' },
    { value: 'EXPIRED', label: 'Expirada' },
  ];

  constructor(
    private payment3dsService: Payment3dsService,
    private modalService: NgbModal,
    private route: ActivatedRoute,
  ) {}

  ngOnInit(): void {
    this.loadData();
    // Deep-link: cuando se entra desde "Parámetros de Sistema → Configuración 3DS"
    // la ruta trae data.startTab = 'config' para abrir directo esa pestaña.
    const startTab = this.route.snapshot.data['startTab'];
    if (startTab === 'config') {
      this.onTabChange('config');
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  async loadData() {
    await Promise.all([
      this.loadStatistics(),
      this.loadTransactions(),
    ]);
  }

  // ================================================================
  // KPIs / Estadisticas
  // ================================================================
  async loadStatistics() {
    this.loadingStats = true;
    try {
      const params: any = {};
      if (this.filters.dateFrom) params.dateFrom = this.filters.dateFrom;
      if (this.filters.dateTo) params.dateTo = this.filters.dateTo;
      const resp = await this.payment3dsService.getStatistics(params);
      this.stats = resp?.response || {};
    } catch (err: any) {
      console.error('Error cargando estadisticas:', err);
    } finally {
      this.loadingStats = false;
    }
  }

  // ================================================================
  // Transacciones
  // ================================================================
  async loadTransactions() {
    this.loadingTransactions = true;
    try {
      const params: any = {
        page: this.pagination.page,
        limit: this.pagination.limit,
      };
      if (this.filters.status) params.status = this.filters.status;
      if (this.filters.dateFrom) params.dateFrom = this.filters.dateFrom;
      if (this.filters.dateTo) params.dateTo = this.filters.dateTo;
      if (this.filters.search) params.search = this.filters.search;

      const resp = await this.payment3dsService.getTransactions(params);
      const data = resp?.response || {};
      this.transactions = data.transactions || [];
      this.pagination = {
        ...this.pagination,
        total: data.pagination?.total || 0,
        pages: data.pagination?.pages || 0,
      };
    } catch (err: any) {
      console.error('Error cargando transacciones:', err);
      alertify.error('Error cargando transacciones');
    } finally {
      this.loadingTransactions = false;
    }
  }

  applyFilters() {
    this.pagination.page = 1;
    this.loadTransactions();
    this.loadStatistics();
  }

  clearFilters() {
    this.filters = { status: '', dateFrom: '', dateTo: '', search: '' };
    this.applyFilters();
  }

  goToPage(page: number) {
    if (page < 1 || page > this.pagination.pages) return;
    this.pagination.page = page;
    this.loadTransactions();
  }

  get visiblePages(): number[] {
    const pages: number[] = [];
    const total = this.pagination.pages;
    const current = this.pagination.page;
    let start = Math.max(1, current - 2);
    let end = Math.min(total, start + 4);
    start = Math.max(1, end - 4);
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    return pages;
  }

  // ================================================================
  // Detalle
  // ================================================================
  async openDetail(txn: any, modal: any) {
    this.selectedTransaction = txn;
    this.auditTrail = [];
    this.loadingDetail = true;
    this.modalService.open(modal, { size: 'lg', scrollable: true });

    try {
      const resp = await this.payment3dsService.getTransactionDetail(txn._id);
      const data = resp?.response || {};
      this.selectedTransaction = data.transaction || txn;
      this.auditTrail = data.auditTrail || [];
    } catch (err: any) {
      console.error('Error cargando detalle:', err);
    } finally {
      this.loadingDetail = false;
    }
  }

  // ================================================================
  // Configuracion
  // ================================================================
  async loadConfig() {
    this.loadingConfig = true;
    try {
      const resp = await this.payment3dsService.getConfig();
      this.config = resp?.response || {};
    } catch (err: any) {
      console.error('Error cargando config:', err);
    } finally {
      this.loadingConfig = false;
    }
  }

  async toggleActive() {
    const current = this.config?.croem_3ds_active?.value === true;
    try {
      await this.payment3dsService.updateConfig({
        key: 'croem_3ds_active',
        value: !current,
        description: 'Activar/desactivar pagos con tarjeta internacional',
      });
      alertify.success(`Pagos 3DS ${!current ? 'activados' : 'desactivados'}`);
      await this.loadConfig();
    } catch (err: any) {
      alertify.error('Error actualizando configuracion');
    }
  }

  async saveConfigValue(key: string, value: any, description: string) {
    try {
      await this.payment3dsService.updateConfig({ key, value, description });
      alertify.success('Configuracion guardada');
      await this.loadConfig();
    } catch (err: any) {
      alertify.error('Error guardando configuracion');
    }
  }

  onTabChange(tab: 'transactions' | 'config') {
    this.activeTab = tab;
    if (tab === 'config' && !this.config?.croem_3ds_active) {
      this.loadConfig();
    }
  }

  // ================================================================
  // Registro de pago en credito
  // ================================================================
  registeringPayment: Record<string, boolean> = {};

  isLinked(txn: any): boolean {
    return !!txn.kreditPaymentCode;
  }

  canRegister(txn: any): boolean {
    return txn.status === 'SALE_COMPLETED' && !txn.kreditPaymentCode;
  }

  async registerPayment(txn: any) {
    if (this.registeringPayment[txn.clientTracking]) return;
    this.registeringPayment[txn.clientTracking] = true;
    try {
      const resp = await this.payment3dsService.registerPaymentForTransaction({
        clientTracking: txn.clientTracking,
        personCode: txn.personCode,
      });
      alertify.success('Pago registrado: ' + (resp?.paymentCode || 'OK'));
      await Promise.all([this.loadTransactions(), this.loadStatistics()]);
    } catch (err: any) {
      const msg = err?.error?.msg || err?.error?.message || 'Error registrando pago';
      alertify.error(msg);
    } finally {
      this.registeringPayment[txn.clientTracking] = false;
    }
  }

  // ================================================================
  // Helpers
  // ================================================================
  getStatusLabel(status: string): string {
    const found = this.statuses.find(s => s.value === status);
    return found ? found.label : status;
  }

  getStatusClass(status: string): string {
    switch (status) {
      case 'SALE_COMPLETED': return 'bg-green-100 text-green-800';
      case 'SALE_FAILED': return 'bg-red-100 text-red-800';
      case 'EXPIRED': return 'bg-gray-100 text-gray-800';
      case 'CHALLENGE_PENDING': return 'bg-yellow-100 text-yellow-800';
      case 'INITIATED':
      case 'AUTHENTICATED':
      case 'CHALLENGE_COMPLETED':
      case 'PENDING_SALE': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-600';
    }
  }

  formatDate(date: string): string {
    if (!date) return '-';
    return new Date(date).toLocaleString('es-VE', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  }

  formatUsd(amount: number): string {
    if (amount === null || amount === undefined) return '$0.00';
    return '$' + amount.toFixed(2);
  }
}
